import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  PenTool, Upload, Trash2, Loader2, User, Search, 
  Check, X, Image, FileImage, Eye, RefreshCw
} from "lucide-react";

export default function FirmaDigitalPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadType, setUploadType] = useState("firma"); // "firma" or "sello"
  const [uploading, setUploading] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/firma-digital/usuarios`, getAuthHeaders());
      setUsuarios(res.data);
    } catch (err) {
      console.error("Error loading users:", err);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const handleOpenUpload = (user, type) => {
    setSelectedUser(user);
    setUploadType(type);
    setShowUploadDialog(true);
  };

  const processFile = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos JPEG, PNG o GIF");
      return;
    }

    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      toast.error("El archivo no debe superar 500KB");
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tipo", uploadType);

      await axios.post(
        `${API}/firma-digital/usuarios/${selectedUser.id}/upload-firma`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success(`${uploadType === "firma" ? "Firma" : "Sello"} actualizado correctamente`);
      setShowUploadDialog(false);
      fetchUsuarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir archivo");
    } finally {
      setUploading(false);
      setIsDragging(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    await processFile(file);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the drop zone entirely
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await processFile(files[0]);
    }
  };

  const handleDelete = async (userId, tipo) => {
    if (!window.confirm(`¿Está seguro de eliminar ${tipo === "firma" ? "la firma" : "el sello"} de este usuario?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/firma-digital/usuarios/${userId}/${tipo}`, getAuthHeaders());
      toast.success(`${tipo === "firma" ? "Firma" : "Sello"} eliminado correctamente`);
      fetchUsuarios();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const handlePreview = (imageUrl, userName, tipo) => {
    setPreviewImage({
      url: imageUrl,
      title: `${tipo === "firma" ? "Firma" : "Sello"} de ${userName}`
    });
    setShowPreviewDialog(true);
  };

  const filteredUsuarios = usuarios.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.departamento?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-6 overflow-auto" data-testid="firma-digital-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <PenTool className="h-7 w-7 text-blue-600" />
                Firma Digital
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Administre las firmas y sellos digitales de los usuarios
              </p>
            </div>
            <Button variant="outline" onClick={fetchUsuarios}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>

          {/* Search */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, email o departamento..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="mb-6 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <FileImage className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-blue-800 dark:text-blue-300">Información sobre Firmas Digitales</h3>
                  <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                    Las firmas y sellos se utilizan para firmar solicitudes de Gestión Humana (permisos, vacaciones, tardanzas, etc.).
                    Formatos permitidos: JPEG, PNG, GIF. Tamaño máximo: 500KB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle>Usuarios del Sistema</CardTitle>
              <CardDescription>
                {filteredUsuarios.length} usuarios encontrados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b dark:border-slate-700">
                      <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-300">Usuario</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-600 dark:text-slate-300">Departamento</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-300">Firma</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-300">Sello</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsuarios.map((usuario) => (
                      <tr key={usuario.id} className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                              <User className="w-5 h-5 text-slate-500" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 dark:text-white">{usuario.name}</p>
                              <p className="text-sm text-slate-500">{usuario.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-slate-600 dark:text-slate-400">
                            {usuario.departamento || "-"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {usuario.firma_url ? (
                            <div className="flex items-center justify-center gap-2">
                              <Badge className="bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Configurada
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handlePreview(usuario.firma_url, usuario.name, "firma")}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                              <X className="w-3 h-3 mr-1" />
                              Sin firma
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {usuario.sello_url ? (
                            <div className="flex items-center justify-center gap-2">
                              <Badge className="bg-green-100 text-green-800">
                                <Check className="w-3 h-3 mr-1" />
                                Configurado
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handlePreview(usuario.sello_url, usuario.name, "sello")}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                              <X className="w-3 h-3 mr-1" />
                              Sin sello
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenUpload(usuario, "firma")}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Firma
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenUpload(usuario, "sello")}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Sello
                            </Button>
                            {(usuario.firma_url || usuario.sello_url) && (
                              <div className="flex gap-1">
                                {usuario.firma_url && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700"
                                    onClick={() => handleDelete(usuario.id, "firma")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredUsuarios.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron usuarios</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Subir {uploadType === "firma" ? "Firma" : "Sello"}
              </DialogTitle>
              <DialogDescription>
                {selectedUser && `Para: ${selectedUser.name}`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 scale-[1.02]' 
                    : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => !uploading && fileInputRef.current?.click()}
                data-testid="firma-dropzone"
              >
                {selectedUser && (uploadType === "firma" ? selectedUser.firma_url : selectedUser.sello_url) ? (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-500">Imagen actual:</p>
                    <img 
                      src={uploadType === "firma" ? selectedUser.firma_url : selectedUser.sello_url}
                      alt={uploadType === "firma" ? "Firma actual" : "Sello actual"}
                      className="max-h-32 mx-auto border rounded"
                    />
                    <p className="text-sm text-slate-500">
                      {isDragging ? '¡Suelte el archivo aquí!' : 'Arrastre o haga clic para reemplazar'}
                    </p>
                  </div>
                ) : (
                  <div>
                    {isDragging ? (
                      <>
                        <Upload className="w-12 h-12 mx-auto mb-4 text-blue-500 animate-bounce" />
                        <p className="text-blue-600 dark:text-blue-400 font-medium mb-2">
                          ¡Suelte el archivo aquí!
                        </p>
                      </>
                    ) : (
                      <>
                        <Image className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                        <p className="text-slate-600 dark:text-slate-400 mb-2">
                          Arrastre una imagen aquí o haga clic para seleccionar
                        </p>
                      </>
                    )}
                    <p className="text-xs text-slate-500">
                      JPEG, PNG o GIF (máx. 500KB)
                    </p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  data-testid="firma-file-input"
                />
                {uploading && (
                  <div className="mt-4 flex items-center justify-center text-blue-600">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    <span>Subiendo archivo...</span>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>{previewImage?.title}</DialogTitle>
            </DialogHeader>
            <div className="flex items-center justify-center p-4 bg-white rounded-lg">
              {previewImage && (
                <img 
                  src={previewImage.url} 
                  alt={previewImage.title}
                  className="max-w-full max-h-64 object-contain"
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
